import type { PluginListenerHandle } from '@capacitor/core';
import {
  ALL_APPLE_PRODUCT_IDS,
  Purchases,
  type PurchasesPlugin,
  type StoreProduct,
  type StoreTransaction,
} from '@/native/purchases';
import { authService } from './authService';

/**
 * Buying a subscription through the App Store, end to end.
 *
 * The native plugin (native/purchases.ts) talks to StoreKit; this strings its
 * calls together with the two server round trips that make a purchase ours:
 *
 *   1. fetch the account's billing token from /api/billing/account-token and
 *      attach it to the purchase as Apple's appAccountToken, so Apple's own
 *      notifications can find the account;
 *   2. post the signed transaction to /api/billing/apple/transactions, so the
 *      entitlement is granted at once rather than whenever Apple's
 *      notification lands — and ONLY THEN finish the transaction.
 *
 * That ordering is the one rule here. An unfinished transaction is redelivered
 * by StoreKit on every launch through the transactionUpdated listener, which
 * is exactly the safety net wanted when the server call fails. Finishing
 * before the server has recorded the purchase throws that net away and leaves
 * someone who paid with no record of it. Nothing in this file finishes a
 * transaction on any other condition.
 *
 * The same record-then-finish step handles everything StoreKit delivers out
 * of band — renewals, refunds, Ask to Buy approvals, a purchase completed
 * while the app was killed — and the current entitlements at sign-in and on
 * Restore Purchases, so a reinstall or a new device resolves without a tap.
 *
 * Native only. Nothing here is reachable on the web.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const BILLING_BASE = `${API_BASE_URL}/api/billing`;

/** What the server says after recording a transaction. */
export interface TransactionReceipt {
  /** False for a replay or an already-expired transaction; both are still
   * recorded as far as the device is concerned. */
  applied: boolean;
  reason: string | null;
  /** The effective tier, which is what the UI should render. */
  tier: string;
  stored_tier: string;
  status: string | null;
  current_period_end: string | null;
}

export type SubmitFailure =
  /** The transaction carries another Aleph account's token: restoring into
   * the wrong account. Never finished, so it can still be claimed from the
   * right one. */
  | 'wrong_account'
  /** The server will never accept this transaction (a product it does not
   * sell, or a payload it cannot read). A configuration bug, not a retry. */
  | 'rejected'
  /** The server could not be reached or answered 5xx. StoreKit redelivers. */
  | 'unavailable';

export class TransactionSubmitError extends Error {
  constructor(
    public readonly kind: SubmitFailure,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'TransactionSubmitError';
  }
}

export type PurchaseResult =
  /**
   * `transaction` is what StoreKit handed back. For a deferred change — a
   * downgrade, or monthly ↔ annual — that is the *current* transaction, not
   * the product that was tapped: the switch lands at the next renewal.
   */
  | { outcome: 'purchased'; receipt: TransactionReceipt; transaction: StoreTransaction }
  | { outcome: 'cancelled' }
  | { outcome: 'pending' };

export interface RecordedBatch {
  recorded: Array<{ transaction: StoreTransaction; receipt: TransactionReceipt }>;
  rejected: Array<{ transaction: StoreTransaction; error: unknown }>;
}

/** Apple's own page for the account's subscriptions — the fallback when the
 * in-app sheet cannot be shown, and the destination on the web. */
export const MANAGE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

export interface BillingDeps {
  store: Pick<
    PurchasesPlugin,
    | 'getProducts'
    | 'purchase'
    | 'getCurrentEntitlements'
    | 'restore'
    | 'finishTransaction'
    | 'addListener'
    | 'showManageSubscriptions'
  >;
  /** Opens a URL outside the web view; the fallback for manageSubscriptions. */
  openExternal?: (url: string) => void;
  fetchAccountToken: () => Promise<string>;
  /** Resolves with the receipt on any 2xx; throws TransactionSubmitError otherwise. */
  submitTransaction: (jws: string) => Promise<TransactionReceipt>;
  /** Out-of-band deliveries are only submitted for a signed-in account; the
   * rest stay unfinished and come back on the next launch. */
  isSignedIn: () => boolean;
  log?: (message: string, detail?: unknown) => void;
}

export type RecordedListener = (receipt: TransactionReceipt, transaction: StoreTransaction) => void;

// Apple silently ignores a non-UUID appAccountToken: the purchase succeeds
// with nothing attached. The plugin refuses one too; this is the earlier of
// the two checks, before anything is bought.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const createBillingFlow = (deps: BillingDeps) => {
  const log = deps.log ?? ((message: string, detail?: unknown) => console.warn(message, detail));
  const recordedListeners = new Set<RecordedListener>();

  /** The one place a transaction is finished: after, and only after, the
   * server has recorded it. */
  // StoreKit hands a new purchase to both purchase() and Transaction.updates,
  // and the listener can win the race by a second or more. Each transaction
  // is recorded once: a second request for one still in flight shares it,
  // and one for a transaction recorded within the last minute gets the same
  // receipt back without a second post — so the Subscribe tap sees the real
  // "applied" rather than a replay of its own purchase. The key includes
  // the revocation, because a refunded transaction comes back with the same
  // id and must be recorded again.
  const RECENT_MS = 60_000;
  const inFlight = new Map<string, Promise<TransactionReceipt>>();
  const recent = new Map<string, { receipt: TransactionReceipt; at: number }>();
  const keyOf = (t: StoreTransaction) => `${t.id}${t.revocationDate ? ':revoked' : ''}`;

  const record = (transaction: StoreTransaction): Promise<TransactionReceipt> => {
    const key = keyOf(transaction);
    const pending = inFlight.get(key);
    if (pending) return pending;
    const done = recent.get(key);
    if (done && Date.now() - done.at < RECENT_MS) return Promise.resolve(done.receipt);

    const work = (async () => {
      const receipt = await deps.submitTransaction(transaction.jws);
      await deps.store.finishTransaction({ id: transaction.id });
      recent.set(key, { receipt, at: Date.now() });
      recordedListeners.forEach((listener) => listener(receipt, transaction));
      return receipt;
    })().finally(() => inFlight.delete(key));
    inFlight.set(key, work);
    return work;
  };

  const recordAll = async (transactions: StoreTransaction[]): Promise<RecordedBatch> => {
    const batch: RecordedBatch = { recorded: [], rejected: [] };
    for (const transaction of transactions) {
      try {
        batch.recorded.push({ transaction, receipt: await record(transaction) });
      } catch (error) {
        log(`Transaction ${transaction.id} (${transaction.productId}) not recorded`, error);
        batch.rejected.push({ transaction, error });
      }
    }
    return batch;
  };

  let listening: Promise<PluginListenerHandle> | null = null;
  // Out-of-band deliveries are recorded one at a time, in arrival order. A
  // launch after time in the background can hand over a whole backlog at
  // once, and there is nothing to gain from posting them concurrently — and
  // something to lose, since each goes through an authenticated request.
  let deliveries: Promise<void> = Promise.resolve();

  return {
    /**
     * Hear about every transaction the server records — from a purchase, a
     * restore, a sign-in sync or an out-of-band delivery alike — so the app
     * can refresh its view of the account. Returns the unsubscribe.
     */
    onRecorded: (listener: RecordedListener): (() => void) => {
      recordedListeners.add(listener);
      return () => recordedListeners.delete(listener);
    },

    products: (ids: readonly string[] = ALL_APPLE_PRODUCT_IDS): Promise<StoreProduct[]> =>
      deps.store.getProducts({ ids }).then((result) => result.products),

    purchase: async (productId: string): Promise<PurchaseResult> => {
      const appAccountToken = await deps.fetchAccountToken();
      if (!UUID_PATTERN.test(appAccountToken)) {
        throw new Error(`Billing account token is not a UUID: ${appAccountToken}`);
      }
      const outcome = await deps.store.purchase({ productId, appAccountToken });
      if (outcome.state !== 'purchased') {
        return { outcome: outcome.state };
      }
      return {
        outcome: 'purchased',
        receipt: await record(outcome.transaction),
        transaction: outcome.transaction,
      };
    },

    /** What StoreKit says is active now, recorded. Call at sign-in. */
    syncEntitlements: async (): Promise<RecordedBatch> =>
      recordAll((await deps.store.getCurrentEntitlements()).transactions),

    /** Restore Purchases. */
    restore: async (): Promise<RecordedBatch> => recordAll((await deps.store.restore()).transactions),

    /**
     * Take the customer to where a subscription can be changed or cancelled:
     * the system sheet in-app, or Apple's subscriptions page if the sheet
     * cannot be shown. Resolves once they are back.
     */
    manageSubscriptions: async (): Promise<void> => {
      try {
        await deps.store.showManageSubscriptions();
      } catch (error) {
        log('Subscription management sheet unavailable; opening the App Store instead', error);
        (deps.openExternal ?? ((url) => window.open(url, '_blank', 'noopener')))(
          MANAGE_SUBSCRIPTIONS_URL,
        );
      }
    },

    /**
     * Start handling out-of-band deliveries. Idempotent; the native listener
     * has been running since launch and holds anything that arrived before
     * this attached.
     */
    startListening: (): Promise<PluginListenerHandle> => {
      if (!listening) {
        listening = deps.store.addListener('transactionUpdated', (transaction) => {
          if (!deps.isSignedIn()) {
            log(`Transaction ${transaction.id} arrived with nobody signed in; left unfinished`);
            return;
          }
          deliveries = deliveries.then(() =>
            record(transaction).then(
              () => undefined,
              (error) => {
                log(`Transaction ${transaction.id} (${transaction.productId}) not recorded`, error);
              },
            ),
          );
        });
        listening.catch(() => {
          listening = null;
        });
      }
      return listening;
    },
  };
};

export type BillingFlow = ReturnType<typeof createBillingFlow>;

// --- the real dependencies ---------------------------------------------------

const fetchAccountToken = async (): Promise<string> => {
  const res = await authService.makeAuthenticatedRequest(`${BILLING_BASE}/account-token`, {
    method: 'GET',
  });
  if (!res.ok) {
    throw new Error(`Could not fetch the billing account token: ${res.status} ${res.statusText}`);
  }
  const body: { billing_account_token: string } = await res.json();
  return body.billing_account_token;
};

const submitTransaction = async (jws: string): Promise<TransactionReceipt> => {
  let res: Response;
  try {
    res = await authService.makeAuthenticatedRequest(`${BILLING_BASE}/apple/transactions`, {
      method: 'POST',
      body: JSON.stringify({ signedTransaction: jws }),
    });
  } catch (error) {
    throw new TransactionSubmitError('unavailable', `Could not reach the server: ${String(error)}`);
  }
  if (res.ok) {
    return res.json();
  }
  const detail = await res.text().catch(() => '');
  if (res.status === 403) {
    throw new TransactionSubmitError('wrong_account', detail, res.status);
  }
  if (res.status === 400) {
    throw new TransactionSubmitError('rejected', detail, res.status);
  }
  throw new TransactionSubmitError('unavailable', detail || res.statusText, res.status);
};

export const billingService = createBillingFlow({
  store: Purchases,
  fetchAccountToken,
  submitTransaction,
  isSignedIn: () => authService.hasStoredCredentials(),
});
